/* eslint-disable no-underscore-dangle */
import React, { useContext } from 'react';
import {
  Bookmark,
  CircleSlash, Folder, Move, Pencil, Trash,
} from 'lucide-react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import PermissionLabel from 'components/PermissionsPopup/PermissionLabel';
import { ButtonDotsMenu, Skeleton } from 'xland-ui';
import { initPermission } from 'utils/functions/initPermissions';
import { PERMISSION_SCOPES } from 'constants/permissions';
import { TOOL_DATA_ROOM } from 'constants/tools';
import { getFolderIcon } from '../utils';
import { getFolderName } from '.';
import { DocumentsContext } from '../provider/DocumentsProvider';

function FoldersList() {
  const {
    literals,
    literalsCommon,
    startup,
    actualFolder,
    subfolders,

    setActualFolder,
    setShowCreateOrRename,
    setShowMovePopup,
    handleBookmark,
    handleDelete,

    loading,
    canEdit,
  } = useContext(DocumentsContext);

  const getRowActions = (item) => {
    const permission = initPermission(item, PERMISSION_SCOPES.STARTUP, startup.id, TOOL_DATA_ROOM);

    if (item?.related?.type || !permission.canEdit) {
      return [
        {
          icon: CircleSlash,
          text: literals.folderNotEditable,
          onClick: () => { },
          disabled: true,
        },
      ];
    }
    return [
      { icon: Pencil, text: literalsCommon.edit, onClick: () => setShowCreateOrRename(item) },
      { icon: Move, text: literals.move, onClick: () => setShowMovePopup(item) },
      {
        icon: Trash, mode: 'danger', text: literalsCommon.delete, onClick: () => handleDelete(item),
      },
    ];
  };

  const getBookmarkFill = (item) => {
    if (item._bookmark === 'loading') {
      return 'var(--content-low)';
    }
    if (item._bookmark) {
      return 'var(--content-high)';
    }
    return 'var(--bg-none)';
  };

  const renderDraggableRow = (item, index) => {
    const rowContent = (
      <div className='position-relative d-flex align-items-center pointer h-100'>
        <div className='content-left'>
          <div className='folder-icon'>
            <Folder size={16} />
            {item.related && (
              <span className='folder-icon--type'>{getFolderIcon(item.related.type)}</span>
            )}
          </div>
          <div className='content-info'>
            <span>{getFolderName(item, literals)}</span>
            <PermissionLabel permission={item.permission} />
          </div>
        </div>
        <div className='dots-menu'>
          <Bookmark
            size={18}
            fill={getBookmarkFill(item)}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleBookmark(item);
            }}
          />
          <ButtonDotsMenu
            size={20}
            buttons={getRowActions(item)}
          />
        </div>
      </div>
    );

    return (
      <Draggable
        index={index}
        draggableId={`folder-${item.id}`}
        isDragDisabled={!canEdit || ['recent', 'bookmark'].includes(actualFolder?.id)}
      >
        {
          (draggableProvider, draggableSnapshot) => (
            <>
              <li
                ref={draggableProvider.innerRef}
                {...draggableProvider.draggableProps}
                {...draggableProvider.dragHandleProps}
                className={`li-container ${loading ? 'disabled' : ''}`}
                style={{
                  ...draggableProvider.draggableProps.style,
                  transform: draggableSnapshot.isDragging ? draggableProvider.draggableProps.style.transform : '',
                }}
                onClick={() => setActualFolder(item)}
              >
                {rowContent}
              </li>
              {draggableSnapshot.isDragging && (
                <li className='li-container folder-placeholder item'>
                  {rowContent}
                </li>
              )}
            </>
          )
        }
      </Draggable>
    );
  };

  if (loading) {
    const auxFolders = actualFolder?.stats ? actualFolder.stats.folders : 6;
    return auxFolders ? (
      <ul className='folder-folders'>
        {Array.from(new Array(auxFolders)).map((_, index) => (
          <li key={index} className='li-container d-flex align-items-center'>
            <Folder size={16} className='me-3' />
            <Skeleton key={index} width='70%' height='10px' />
          </li>
        ))}
      </ul>
    ) : null;
  }

  return subfolders.length > 0 && (
    <ul className='folder-folders'>
      {subfolders.map((subfolder, i) => (
        <Droppable key={`subfolder-${subfolder.id}`} droppableId={`subfolder-${subfolder.id}`} isCombineEnabled ignoreContainerClipping>
          {(provider, snapshot) => {
            const isDraggingOver = snapshot.isDraggingOver && `subfolder-${subfolder.id}` !== snapshot.draggingOverWith;
            return (
              <React.Fragment ref={provider.innerRef} className={`droppable ${isDraggingOver ? 'droppable-selected' : ''}`}>
                {renderDraggableRow(subfolder, i)}
                <div className={`folder-dropzone ${isDraggingOver ? 'active' : ''}`}>
                  <span className='fc-secondary'>{literals.dropInside}</span>
                  {provider.placeholder}
                </div>
              </React.Fragment>
            );
          }}
        </Droppable>
      ))}
    </ul>
  );
}

export default FoldersList;
